import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Club, Camera, Sponsor, CreateSponsorRequest, CompetitionTemplate, Competition, CameraScheduleDay, CameraScheduleResponse } from '../../types';
import { clubsApi, camerasApi, sponsorsApi, templatesApi, competitionsApi } from '../../services/api';
import AdvertsTab from './AdvertsTab';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import FormField, { inputStyle } from '../../components/FormField';
import { useUserRoles } from '../../auth/useUserRoles';

type Tab = 'cameras' | 'sponsors' | 'templates' | 'competitions' | 'adverts';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FORMATS = ['Stableford', 'Stroke', 'Par', 'Bogey', 'Ambrose'];
const COMP_TYPES = ['MiScore', 'Adhoc'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function fmtTime(t: string) {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour || 12}:${m}${hour >= 12 ? 'pm' : 'am'}`;
}

function fmtDate(d: string) {
    return new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Tab Bar ───────────────────────────────────────────────

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
    const tabs: { key: Tab; label: string }[] = [
        { key: 'cameras', label: 'Cameras' }, { key: 'sponsors', label: 'Sponsors' },
        { key: 'templates', label: 'Templates' }, { key: 'competitions', label: 'Competitions' },
        { key: 'adverts', label: 'Adverts' },
    ];
    return (
        <div style={tabStyles.bar}>
            {tabs.map(t => (
                <button key={t.key} style={{ ...tabStyles.tab, ...(active === t.key ? tabStyles.activeTab : {}) }} onClick={() => onChange(t.key)}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

const tabStyles: Record<string, React.CSSProperties> = {
    bar: { display: 'flex', gap: 0, borderBottom: '2px solid #eee', marginBottom: 24 },
    tab: { padding: '10px 20px', background: 'none', border: 'none', fontSize: 14, fontWeight: 500, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2, transition: 'all 0.15s' },
    activeTab: { color: '#1a6b3c', borderBottomColor: '#1a6b3c', fontWeight: 700 },
};

// ── Add Camera Form ───────────────────────────────────────

function AddCameraForm({ clubId, onAdded, onClose }: { clubId: string; onAdded: () => void; onClose: () => void }) {
    const [form, setForm] = useState({ label: '', sortOrder: 1, isFeatured: false, streamType: 'Reolink', serialNumber: '', macAddress: '', ltePhoneNumber: '', reolinkUsername: '', reolinkPassword: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showAdminInfo, setShowAdminInfo] = useState(false);
    const set = (f: string, v: any) => setForm(p => ({ ...p, [f]: v }));
    const handleSubmit = async () => {
        if (!form.label.trim()) { setError('Label is required'); return; }
        setSaving(true); setError('');
        try { await camerasApi.add(clubId, form); onAdded(); }
        catch (e: any) { setError(e.message ?? 'Failed to add camera'); }
        finally { setSaving(false); }
    };
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <FormField label="Camera Label" hint="e.g. Lake 9, Hole 18">
                <input style={inputStyle} value={form.label} onChange={e => set('label', e.target.value)} placeholder="Lake 9" autoFocus />
            </FormField>
            <FormField label="Sort Order">
                <input style={inputStyle} type="number" value={form.sortOrder} onChange={e => set('sortOrder', parseInt(e.target.value) || 1)} />
            </FormField>
            <FormField label="Camera Type">
                <select style={inputStyle} value={form.streamType} onChange={e => set('streamType', e.target.value)}>
                    <option value="Reolink">Reolink / Fixed Solar Camera (RTMP)</option>
                    <option value="Larix">Larix / Phone Camera (RTMP)</option>
                    <option value="OBS">OBS / Computer (RTMP)</option>
                    <option value="FtpClips">Atlas Go PT / FTP Clips</option>
                </select>
            </FormField>
            <div style={{ fontSize: 11, color: '#999', marginTop: -12, marginBottom: 16 }}>
                {form.streamType === 'Larix' ? 'Use for phones running the Larix app' : form.streamType === 'OBS' ? 'Use for computers running OBS Studio' : form.streamType === 'FtpClips' ? 'Battery-powered camera that uploads motion clips via FTP. FTP credentials will be generated.' : 'Use for Reolink solar cameras with RTMP push'}
            </div>
            <button type="button" style={styles.advancedToggle} onClick={() => setShowAdminInfo(v => !v)}>
                {showAdminInfo ? '▾' : '▸'} Camera Admin Info (optional)
            </button>
            {showAdminInfo && (
                <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 16, marginTop: 8, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>For SuperUser reference. These details help maintain the camera fleet.</div>
                    <FormField label="Serial Number"><input style={inputStyle} value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} placeholder="e.g. RL12345678" /></FormField>
                    <FormField label="MAC Address"><input style={inputStyle} value={form.macAddress} onChange={e => set('macAddress', e.target.value)} placeholder="e.g. 00:1A:2B:3C:4D:5E" /></FormField>
                    <FormField label="LTE Phone Number"><input style={inputStyle} value={form.ltePhoneNumber} onChange={e => set('ltePhoneNumber', e.target.value)} placeholder="e.g. +61 4XX XXX XXX" /></FormField>
                    <FormField label="Reolink Login Username"><input style={inputStyle} value={form.reolinkUsername} onChange={e => set('reolinkUsername', e.target.value)} placeholder="admin" autoComplete="off" /></FormField>
                    <FormField label="Reolink Login Password"><input style={inputStyle} type="password" value={form.reolinkPassword} onChange={e => set('reolinkPassword', e.target.value)} autoComplete="off" /></FormField>
                    <FormField label="Notes"><textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></FormField>
                </div>
            )}
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Provisioning...' : 'Add Camera'}</Button>
            </div>
        </>
    );
}

// ── Sponsor Form ──────────────────────────────────────────

function SponsorForm({ clubId, sponsor, onSaved, onClose }: { clubId: string; sponsor?: Sponsor; onSaved: () => void; onClose: () => void }) {
    const [form, setForm] = useState<CreateSponsorRequest>({
        name: sponsor?.name ?? '', logoUrl: sponsor?.logoUrl ?? '', clickUrl: sponsor?.clickUrl ?? '',
        displayFrom: sponsor?.displayFrom ?? '', displayTo: sponsor?.displayTo ?? '',
        durationSeconds: sponsor?.durationSeconds ?? 15, sortOrder: sponsor?.sortOrder ?? 1,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (field: keyof CreateSponsorRequest, value: any) => setForm(f => ({ ...f, [field]: value }));
    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Sponsor name is required'); return; }
        setSaving(true); setError('');
        try {
            const data = { ...form, clickUrl: form.clickUrl || undefined, displayFrom: form.displayFrom || undefined, displayTo: form.displayTo || undefined };
            if (sponsor) await sponsorsApi.update(clubId, sponsor.id, data);
            else await sponsorsApi.add(clubId, data);
            onSaved();
        } catch (e: any) { setError(e.message ?? 'Failed to save sponsor'); }
        finally { setSaving(false); }
    };
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <FormField label="Sponsor Name"><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Riverside Real Estate" autoFocus /></FormField>
            <FormField label="Logo URL" hint="Direct link to sponsor logo image"><input style={inputStyle} value={form.logoUrl} onChange={e => set('logoUrl', e.target.value)} placeholder="https://example.com/logo.png" /></FormField>
            <FormField label="Click URL" hint="Optional"><input style={inputStyle} value={form.clickUrl} onChange={e => set('clickUrl', e.target.value)} placeholder="https://example.com" /></FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Display From" hint="Leave blank to always show"><input style={inputStyle} type="time" value={form.displayFrom} onChange={e => set('displayFrom', e.target.value)} /></FormField>
                <FormField label="Display To"><input style={inputStyle} type="time" value={form.displayTo} onChange={e => set('displayTo', e.target.value)} /></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Duration (seconds)"><input style={inputStyle} type="number" value={form.durationSeconds} onChange={e => set('durationSeconds', parseInt(e.target.value) || 15)} /></FormField>
                <FormField label="Sort Order"><input style={inputStyle} type="number" value={form.sortOrder} onChange={e => set('sortOrder', parseInt(e.target.value) || 1)} /></FormField>
            </div>
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : sponsor ? 'Update Sponsor' : 'Add Sponsor'}</Button>
            </div>
        </>
    );
}

// ── Template Form ─────────────────────────────────────────

function TemplateForm({ clubId, template, cameras, sponsors, onSaved, onClose }: { clubId: string; template?: CompetitionTemplate; cameras: Camera[]; sponsors: Sponsor[]; onSaved: () => void; onClose: () => void }) {
    const [form, setForm] = useState({
        name: template?.name ?? '', dayOfWeek: template?.dayOfWeek ?? '' as any, format: template?.format ?? 0, type: template?.type ?? 0,
        firstTeeTime: template?.firstTeeTime ?? '07:00:00', lastTeeTime: template?.lastTeeTime ?? '10:00:00',
        expectedFinishTime: template?.expectedFinishTime ?? '14:00:00', cameraStartDelayMinutes: template?.cameraStartDelayMinutes ?? 90,
        leaderboardDisplayMinutes: template?.leaderboardDisplayMinutes ?? 120, leaderboardRotationMinutes: template?.leaderboardRotationMinutes ?? 15,
        leaderboardDurationSeconds: template?.leaderboardDurationSeconds ?? 60, cameraRotationSeconds: template?.cameraRotationSeconds ?? 30,
        tickerSlideFrequencySeconds: template?.tickerSlideFrequencySeconds ?? 20,
        showPars: template?.showPars ?? true, showBirdies: template?.showBirdies ?? true, showEagles: template?.showEagles ?? true,
        cameraIds: template?.templateCameras?.map(tc => tc.cameraId) ?? [] as string[],
        sponsorIds: template?.templateSponsors?.map(ts => ts.sponsorId) ?? [] as string[],
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
    const toggleCamera = (id: string) => setForm(f => ({ ...f, cameraIds: f.cameraIds.includes(id) ? f.cameraIds.filter(c => c !== id) : [...f.cameraIds, id] }));
    const toggleSponsor = (id: string) => setForm(f => ({ ...f, sponsorIds: f.sponsorIds.includes(id) ? f.sponsorIds.filter(s => s !== id) : [...f.sponsorIds, id] }));
    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Name is required'); return; }
        setSaving(true); setError('');
        try {
            const payload = { ...form, dayOfWeek: form.dayOfWeek === '' ? null : parseInt(form.dayOfWeek) };
            if (template) await templatesApi.update(clubId, template.id, payload);
            else await templatesApi.create(clubId, payload);
            onSaved();
        } catch (e: any) { setError(e.message ?? 'Failed to save template'); }
        finally { setSaving(false); }
    };
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Template Name"><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Saturday Medal AM" autoFocus /></FormField>
                <FormField label="Day of Week" hint="Leave blank for adhoc">
                    <select style={inputStyle} value={form.dayOfWeek} onChange={e => set('dayOfWeek', e.target.value)}>
                        <option value="">Adhoc / No fixed day</option>
                        {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                </FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Format"><select style={inputStyle} value={form.format} onChange={e => set('format', parseInt(e.target.value))}>{FORMATS.map((f, i) => <option key={i} value={i}>{f}</option>)}</select></FormField>
                <FormField label="Type"><select style={inputStyle} value={form.type} onChange={e => set('type', parseInt(e.target.value))}>{COMP_TYPES.map((t, i) => <option key={i} value={i}>{t}</option>)}</select></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <FormField label="First Tee Time"><input style={inputStyle} type="time" value={form.firstTeeTime.substring(0, 5)} onChange={e => set('firstTeeTime', e.target.value + ':00')} /></FormField>
                <FormField label="Last Tee Time"><input style={inputStyle} type="time" value={form.lastTeeTime.substring(0, 5)} onChange={e => set('lastTeeTime', e.target.value + ':00')} /></FormField>
                <FormField label="Expected Finish"><input style={inputStyle} type="time" value={form.expectedFinishTime?.substring(0, 5) ?? ''} onChange={e => set('expectedFinishTime', e.target.value + ':00')} /></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Camera Start Delay (mins)" hint="After first tee time"><input style={inputStyle} type="number" value={form.cameraStartDelayMinutes} onChange={e => set('cameraStartDelayMinutes', parseInt(e.target.value) || 0)} /></FormField>
                <FormField label="Leaderboard Display (mins)" hint="After competition ends"><input style={inputStyle} type="number" value={form.leaderboardDisplayMinutes} onChange={e => set('leaderboardDisplayMinutes', parseInt(e.target.value) || 120)} /></FormField>
            </div>
            <FormField label="Cameras">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    {cameras.map(cam => (
                        <label key={cam.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.cameraIds.includes(cam.id)} onChange={() => toggleCamera(cam.id)} />
                            <span style={{ fontSize: 14 }}>{cam.label}</span>
                            <span style={{ fontSize: 11, color: '#999' }}>{cam.streamType === 1 ? 'Larix' : cam.streamType === 2 ? 'OBS' : 'Reolink'}</span>
                        </label>
                    ))}
                    {cameras.length === 0 && <div style={{ fontSize: 13, color: '#999' }}>No cameras configured for this club</div>}
                </div>
            </FormField>
            <FormField label="Default Sponsors" hint="Sponsors shown during this competition">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    {sponsors.map(s => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.sponsorIds.includes(s.id)} onChange={() => toggleSponsor(s.id)} />
                            <span style={{ fontSize: 14 }}>{s.name}</span>
                        </label>
                    ))}
                    {sponsors.length === 0 && <div style={{ fontSize: 13, color: '#999' }}>No sponsors configured for this club</div>}
                </div>
            </FormField>
            <button style={{ ...styles.advancedToggle }} onClick={() => setShowAdvanced(v => !v)}>
                {showAdvanced ? '▾' : '▸'} Advanced Display Settings
            </button>
            {showAdvanced && (
                <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 16, marginTop: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <FormField label="Leaderboard Rotation (mins)"><input style={inputStyle} type="number" value={form.leaderboardRotationMinutes} onChange={e => set('leaderboardRotationMinutes', parseInt(e.target.value) || 15)} /></FormField>
                        <FormField label="Leaderboard Duration (secs)"><input style={inputStyle} type="number" value={form.leaderboardDurationSeconds} onChange={e => set('leaderboardDurationSeconds', parseInt(e.target.value) || 60)} /></FormField>
                        <FormField label="Camera Rotation (secs)"><input style={inputStyle} type="number" value={form.cameraRotationSeconds} onChange={e => set('cameraRotationSeconds', parseInt(e.target.value) || 30)} /></FormField>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><input type="checkbox" checked={form.showPars} onChange={e => set('showPars', e.target.checked)} />Show Pars in ticker</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><input type="checkbox" checked={form.showBirdies} onChange={e => set('showBirdies', e.target.checked)} />Show Birdies in ticker</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><input type="checkbox" checked={form.showEagles} onChange={e => set('showEagles', e.target.checked)} />Show Eagles in ticker</label>
                    </div>
                </div>
            )}
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}</Button>
            </div>
        </>
    );
}

// ── Apply Template Modal ──────────────────────────────────

function ApplyTemplateModal({ clubId, templates, onApplied, onClose }: { clubId: string; templates: CompetitionTemplate[]; onApplied: () => void; onClose: () => void }) {
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(nextMonth);
    const [preview, setPreview] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const selectedTemplate = templates.find(t => t.id === templateId);
    useEffect(() => {
        if (!templateId || !startDate || !endDate) return;
        const template = templates.find(t => t.id === templateId);
        if (!template) return;
        const dates: string[] = [];
        const current = new Date(startDate);
        const end = new Date(endDate);
        while (current <= end) {
            if (template.dayOfWeek === null || template.dayOfWeek === undefined || current.getDay() === template.dayOfWeek)
                dates.push(current.toISOString().split('T')[0]);
            current.setDate(current.getDate() + 1);
        }
        setPreview(dates);
    }, [templateId, startDate, endDate, templates]);
    const handleApply = async () => {
        if (!templateId) { setError('Select a template'); return; }
        if (preview.length === 0) { setError('No dates match this template in the selected range'); return; }
        setSaving(true); setError('');
        try { await competitionsApi.applyTemplate(clubId, { templateId, startDate, endDate }); onApplied(); }
        catch (e: any) { setError(e.message ?? 'Failed to apply template'); }
        finally { setSaving(false); }
    };
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <FormField label="Template">
                <select style={inputStyle} value={templateId} onChange={e => setTemplateId(e.target.value)}>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name} {t.dayOfWeek != null ? `(${DAYS[t.dayOfWeek]}s)` : '(Adhoc)'}</option>)}
                </select>
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Start Date"><input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></FormField>
                <FormField label="End Date"><input style={inputStyle} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></FormField>
            </div>
            {selectedTemplate && (
                <div style={{ background: '#f0f7f3', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a6b3c', marginBottom: 4 }}>TEMPLATE DETAILS</div>
                    <div style={{ fontSize: 13, color: '#444' }}>{FORMATS[selectedTemplate.format]} · {COMP_TYPES[selectedTemplate.type]} · First tee: {fmtTime(selectedTemplate.firstTeeTime)} · Finish: {fmtTime(selectedTemplate.expectedFinishTime ?? '')}</div>
                </div>
            )}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginBottom: 8 }}>COMPETITIONS TO CREATE ({preview.length})</div>
                {preview.length === 0 ? <div style={{ fontSize: 13, color: '#999' }}>No matching dates in selected range</div> : (
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
                        {preview.map(d => <div key={d} style={{ fontSize: 13, padding: '4px 8px', borderBottom: '1px solid #f5f5f5' }}>{fmtDate(d)}</div>)}
                    </div>
                )}
            </div>
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleApply} disabled={saving || preview.length === 0}>{saving ? 'Creating...' : `Create ${preview.length} Competition${preview.length !== 1 ? 's' : ''}`}</Button>
            </div>
        </>
    );
}

// ── Camera Schedule Grid ──────────────────────────────────

function CameraScheduleGrid({ days, onChange }: { days: CameraScheduleDay[]; onChange: (days: CameraScheduleDay[]) => void }) {
    const updateDay = (dow: number, patch: Partial<CameraScheduleDay>) => onChange(days.map(d => d.dayOfWeek === dow ? { ...d, ...patch } : d));
    const setMode = (dow: number, mode: 'open' | 'closed' | 'custom') => {
        if (mode === 'open') updateDay(dow, { closed: false, windows: [] });
        else if (mode === 'closed') updateDay(dow, { closed: true, windows: [] });
        else updateDay(dow, { closed: false, windows: days.find(d => d.dayOfWeek === dow)?.windows.length ? days.find(d => d.dayOfWeek === dow)!.windows : [{ startTime: '11:00', endTime: '19:00' }] });
    };
    const modeOf = (d: CameraScheduleDay): 'open' | 'closed' | 'custom' => d.closed ? 'closed' : d.windows.length > 0 ? 'custom' : 'open';
    const addWindow = (dow: number) => { const d = days.find(x => x.dayOfWeek === dow)!; updateDay(dow, { windows: [...d.windows, { startTime: '11:00', endTime: '19:00' }] }); };
    const removeWindow = (dow: number, idx: number) => { const d = days.find(x => x.dayOfWeek === dow)!; updateDay(dow, { windows: d.windows.filter((_, i) => i !== idx), closed: false }); };
    const updateWindow = (dow: number, idx: number, field: 'startTime' | 'endTime', val: string) => { const d = days.find(x => x.dayOfWeek === dow)!; updateDay(dow, { windows: d.windows.map((w, i) => i === idx ? { ...w, [field]: val } : w) }); };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3, 4, 5, 6, 0].map(dow => {
                const d = days.find(x => x.dayOfWeek === dow) ?? { dayOfWeek: dow, closed: false, windows: [] };
                const mode = modeOf(d);
                return (
                    <div key={dow} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 10px', background: '#fafafa', borderRadius: 6, border: '1px solid #eee' }}>
                        <div style={{ width: 90, fontWeight: 600, fontSize: 13, paddingTop: 6 }}>{DAY_NAMES[dow]}</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: 6, marginBottom: mode === 'custom' ? 8 : 0 }}>
                                {(['open', 'closed', 'custom'] as const).map(m => (
                                    <button key={m} type="button" onClick={() => setMode(dow, m)} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 5, border: mode === m ? '1px solid #1a6b3c' : '1px solid #ccc', background: mode === m ? '#1a6b3c' : '#fff', color: mode === m ? '#fff' : '#444', cursor: 'pointer', fontWeight: 600 }}>
                                        {m === 'open' ? 'Open all day' : m === 'closed' ? 'Closed' : 'Custom hours'}
                                    </button>
                                ))}
                            </div>
                            {mode === 'custom' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {d.windows.map((w, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <input type="time" value={w.startTime} onChange={e => updateWindow(dow, i, 'startTime', e.target.value)} style={{ ...inputStyle, width: 120 }} />
                                            <span style={{ color: '#888' }}>–</span>
                                            <input type="time" value={w.endTime} onChange={e => updateWindow(dow, i, 'endTime', e.target.value)} style={{ ...inputStyle, width: 120 }} />
                                            <button type="button" onClick={() => removeWindow(dow, i)} style={{ border: 'none', background: 'none', color: '#c00', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => addWindow(dow)} style={{ alignSelf: 'flex-start', padding: '3px 10px', fontSize: 12, border: '1px dashed #1a6b3c', borderRadius: 5, background: '#fff', color: '#1a6b3c', cursor: 'pointer' }}>+ Add window</button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Green Region Editor ───────────────────────────────────────────────────────
// Displays the camera's background still and lets the admin draw a rectangle
// to define the green boundary for flag detection and zoom clamping.

function GreenRegionEditor({
    cameraId,
    clubId,
    rtmpKey,
    greenRegionX,
    greenRegionY,
    greenRegionW,
    greenRegionH,
    onChange,
}: {
    cameraId: string;
    clubId: string;
    rtmpKey: string;
    greenRegionX: number | null;
    greenRegionY: number | null;
    greenRegionW: number | null;
    greenRegionH: number | null;
    onChange: (x: number, y: number, w: number, h: number) => void;
}) {
    const [stillUrl, setStillUrl] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [drawing, setDrawing] = React.useState(false);
    const [startPt, setStartPt] = React.useState<{ x: number; y: number } | null>(null);
    const [rect, setRect] = React.useState<{ x: number; y: number; w: number; h: number } | null>(
        greenRegionX != null && greenRegionW != null
            ? { x: greenRegionX, y: greenRegionY ?? 0, w: greenRegionW, h: greenRegionH ?? 200 }
            : null
    );
    const imgRef = React.useRef<HTMLImageElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Natural image dimensions (1920x1080) — needed to scale coords
    const [naturalW, setNaturalW] = React.useState(1920);
    const [naturalH, setNaturalH] = React.useState(1080);

    React.useEffect(() => {
        if (!rtmpKey) {
            setError('No still available yet — upload a clip first');
            setLoading(false);
            return;
        }
        const url = `https://stream.19thview.com.au/clips/archive/${rtmpKey}/background.jpg`;
        fetch(url, { method: 'HEAD' })
            .then(r => {
                if (r.ok) { setStillUrl(url); }
                else { setError('No still available yet — upload a clip first'); }
                setLoading(false);
            })
            .catch(() => {
                setError('No still available yet — upload a clip first');
                setLoading(false);
            });
    }, [rtmpKey]);

    // Convert display coords → natural image coords
    const toNatural = (dx: number, dy: number) => {
        const el = imgRef.current;
        if (!el) return { x: dx, y: dy };
        const scaleX = naturalW / el.clientWidth;
        const scaleY = naturalH / el.clientHeight;
        return { x: Math.round(dx * scaleX), y: Math.round(dy * scaleY) };
    };

    // Convert natural image coords → display coords
    const toDisplay = (nx: number, ny: number) => {
        const el = imgRef.current;
        if (!el) return { x: nx, y: ny };
        const scaleX = el.clientWidth / naturalW;
        const scaleY = el.clientHeight / naturalH;
        return { x: nx * scaleX, y: ny * scaleY };
    };

    const getRelPos = (e: React.MouseEvent) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };
        const bounds = container.getBoundingClientRect();
        return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    };

    const onImageLoad = () => {
        if (greenRegionX != null && imgRef.current) {
            const tl = toDisplay(greenRegionX, greenRegionY ?? 0);
            const br = toDisplay(greenRegionX + (greenRegionW ?? 0), (greenRegionY ?? 0) + (greenRegionH ?? 0));
            setRect({ x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y });
        }
    };

    const onMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setDrawing(true);
        setStartPt(getRelPos(e));
        setRect(null);
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!drawing || !startPt) return;
        const curr = getRelPos(e);
        const x = Math.min(startPt.x, curr.x);
        const y = Math.min(startPt.y, curr.y);
        const w = Math.abs(curr.x - startPt.x);
        const h = Math.abs(curr.y - startPt.y);
        setRect({ x, y, w, h });
    };

    const onMouseUp = (e: React.MouseEvent) => {
        if (!drawing || !startPt) return;
        setDrawing(false);
        const curr = getRelPos(e);
        const dispX = Math.min(startPt.x, curr.x);
        const dispY = Math.min(startPt.y, curr.y);
        const dispW = Math.abs(curr.x - startPt.x);
        const dispH = Math.abs(curr.y - startPt.y);
        if (dispW < 20 || dispH < 10) { setRect(null); return; }
        // Convert to natural image coordinates
        const topLeft = toNatural(dispX, dispY);
        const bottomRight = toNatural(dispX + dispW, dispY + dispH);
        const natX = topLeft.x;
        const natY = topLeft.y;
        const natW = bottomRight.x - topLeft.x;
        const natH = bottomRight.y - topLeft.y;
        setRect({ x: dispX, y: dispY, w: dispW, h: dispH });
        onChange(natX, natY, natW, natH);
    };


    // If we have natural coords from props, compute display rect
    const propDisplayRect = greenRegionX != null && imgRef.current
        ? (() => {
            const tl = toDisplay(greenRegionX, greenRegionY ?? 0);
            const br = toDisplay(greenRegionX + (greenRegionW ?? 0), (greenRegionY ?? 0) + (greenRegionH ?? 0));
            return { x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y };
        })()
        : null;

    const shownRect = rect ?? propDisplayRect;

    return (
        <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginBottom: 6, letterSpacing: 0.5 }}>
                GREEN REGION
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                Draw a rectangle over the green area. This constrains flag detection and keeps the zoom on the playing surface.
                {greenRegionX != null && (
                    <span style={{ color: '#1a6b3c', marginLeft: 8 }}>
                        ✓ Set ({greenRegionX}, {greenRegionY}, {greenRegionW}×{greenRegionH})
                    </span>
                )}
            </div>
            {loading && <div style={{ fontSize: 13, color: '#999', padding: 10 }}>Loading camera still…</div>}
            {error && <div style={{ fontSize: 13, color: '#c00' }}>{error}</div>}
            {stillUrl && !loading && (
                <div
                    ref={containerRef}
                    style={{ position: 'relative', cursor: 'crosshair', userSelect: 'none', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={() => { if (drawing) setDrawing(false); }}
                >
                <img
                    ref={imgRef}
                    src={stillUrl}
                    alt="Camera still"
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                    onLoad={e => {
                        const img = e.target as HTMLImageElement;
                        setNaturalW(img.naturalWidth);
                        setNaturalH(img.naturalHeight);
                        onImageLoad();
                    }}
                    draggable={false}
                />
                    {shownRect && (
                        <div style={{
                            position: 'absolute',
                            left: shownRect.x,
                            top: shownRect.y,
                            width: shownRect.w,
                            height: shownRect.h,
                            border: '2px solid #1a6b3c',
                            background: 'rgba(26,107,60,0.15)',
                            pointerEvents: 'none',
                        }}>
                            <div style={{
                                position: 'absolute', top: 2, left: 4,
                                fontSize: 10, fontWeight: 700, color: '#1a6b3c',
                                background: 'rgba(255,255,255,0.8)', padding: '1px 4px', borderRadius: 2,
                            }}>
                                GREEN REGION
                            </div>
                        </div>
                    )}
                    {greenRegionX == null && !rect && (
                        <div style={{
                            position: 'absolute', inset: 0, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <div style={{
                                background: 'rgba(0,0,0,0.5)', color: '#fff',
                                padding: '8px 16px', borderRadius: 6, fontSize: 13,
                            }}>
                                Click and drag to draw the green region
                            </div>
                        </div>
                    )}
                </div>
            )}
            {greenRegionX != null && (
                <button
                    type="button"
                    style={{ marginTop: 8, fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => { setRect(null); onChange(0, 0, 0, 0); }}
                >
                    ✕ Clear green region
                </button>
            )}
        </div>
    );
}


// ── Edit Camera Form ──────────────────────────────────────

function EditCameraForm({ camera, clubId, onSaved, onClose }: { camera: Camera; clubId: string; onSaved: () => void; onClose: () => void }) {
    const [form, setForm] = useState({
        label: camera.label, sortOrder: camera.sortOrder, holeNumber: camera.holeNumber ?? '',
        par: camera.par ?? '', distanceMetres: camera.distanceMetres ?? '', holeName: camera.holeName ?? '',
        priority: camera.priority ?? 5, serialNumber: camera.serialNumber ?? '', macAddress: camera.macAddress ?? '',
        ltePhoneNumber: camera.ltePhoneNumber ?? '', reolinkUsername: camera.reolinkUsername ?? '',
        reolinkPassword: camera.reolinkPassword ?? '', notes: camera.notes ?? '',
        zoomLevel: camera.zoomLevel ?? null,
        zoomStartPct: camera.zoomStartPct ?? 20,
        zoomEndPct: camera.zoomEndPct ?? 10,
        zoom2Level: camera.zoom2Level ?? null,
        zoom2Pct: camera.zoom2Pct ?? null,
        transitionSecs: camera.transitionSecs ?? 2,
        zoomResetAtEnd: camera.zoomResetAtEnd ?? true,
        zoomResetSecs: camera.zoomResetSecs ?? 15,
        greenRegionX: camera.greenRegionX ?? null,
        greenRegionY: camera.greenRegionY ?? null,
        greenRegionW: camera.greenRegionW ?? null,
        greenRegionH: camera.greenRegionH ?? null,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [schedule, setSchedule] = useState<CameraScheduleDay[]>(Array.from({ length: 7 }, (_, dow) => ({ dayOfWeek: dow, closed: false, windows: [] })));
    const [scheduleLoaded, setScheduleLoaded] = useState(false);
    useEffect(() => {
        let cancelled = false;
        camerasApi.getSchedule(clubId, camera.id)
            .then((resp: CameraScheduleResponse) => { if (!cancelled && resp?.days) { setSchedule(resp.days); setScheduleLoaded(true); } })
            .catch(() => setScheduleLoaded(true));
        return () => { cancelled = true; };
    }, [clubId, camera.id]);
    const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
    const handleSubmit = async () => {
        if (!form.label.trim()) { setError('Label is required'); return; }
        setSaving(true); setError('');
        try {
            const payload = { ...form, holeNumber: form.holeNumber === '' || form.holeNumber === null ? null : Number(form.holeNumber), par: form.par === '' || form.par === null ? null : Number(form.par), distanceMetres: form.distanceMetres === '' || form.distanceMetres === null ? null : Number(form.distanceMetres) };
            await camerasApi.update(clubId, camera.id, payload);
            await camerasApi.updateSchedule(clubId, camera.id, schedule);
            onSaved();
        } catch (e: any) { setError(e.message ?? 'Failed to update camera'); }
        finally { setSaving(false); }
    };
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Camera Label"><input style={inputStyle} value={form.label} onChange={e => set('label', e.target.value)} autoFocus /></FormField>
                <FormField label="Sort Order"><input style={inputStyle} type="number" value={form.sortOrder} onChange={e => set('sortOrder', parseInt(e.target.value) || 1)} /></FormField>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 16, marginBottom: 8, letterSpacing: 0.5 }}>HOLE INFORMATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                <FormField label="Hole Number"><input style={inputStyle} type="number" value={form.holeNumber} onChange={e => set('holeNumber', e.target.value === '' ? '' : parseInt(e.target.value))} placeholder="e.g. 9" /></FormField>
                <FormField label="Par"><input style={inputStyle} type="number" value={form.par} onChange={e => set('par', e.target.value === '' ? '' : parseInt(e.target.value))} placeholder="e.g. 4" /></FormField>
                <FormField label="Distance (m)"><input style={inputStyle} type="number" value={form.distanceMetres} onChange={e => set('distanceMetres', e.target.value === '' ? '' : parseInt(e.target.value))} placeholder="e.g. 380" /></FormField>
                <FormField label="Priority" hint="Lower = preferred (0-9)"><input style={inputStyle} type="number" value={form.priority} onChange={e => set('priority', parseInt(e.target.value) || 5)} /></FormField>
            </div>
            <FormField label="Hole Name" hint="Optional, e.g. 'The Gap'"><input style={inputStyle} value={form.holeName} onChange={e => set('holeName', e.target.value)} /></FormField>

            {camera.streamType === 4 && (
            <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 16, marginBottom: 8, letterSpacing: 0.5 }}>DIGITAL ZOOM</div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Source is 2K — up to 133% is lossless. Beyond that quality reduces slightly.</div>

                <FormField label="First Zoom Level">
                    <select style={inputStyle} 
                            value={form.zoomLevel != null ? String(form.zoomLevel) : ''} 
                            onChange={e => set('zoomLevel', e.target.value ? parseFloat(e.target.value) : null)}>
                        <option value="">No zoom (wide shot only)</option>
                        <option value="1.33">133% — lossless (recommended)</option>
                        <option value="1.5">150%</option>
                        <option value="1.66">166%</option>
                        <option value="2">200%</option>
                    </select>
                </FormField>

                {form.zoomLevel && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <FormField label="Zoom in at (% of clip)" hint="e.g. 25 = zoom starts 25% into clip">
                                <input style={inputStyle} type="number" min={5} max={80} value={form.zoomStartPct}
                                    onChange={e => set('zoomStartPct', parseInt(e.target.value) || 25)} />
                            </FormField>
                            <FormField label="Transition duration (secs)">
                                <input style={inputStyle} type="number" min={0.5} max={5} step={0.5} value={form.transitionSecs}
                                    onChange={e => set('transitionSecs', parseFloat(e.target.value) || 2)} />
                            </FormField>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 12, marginBottom: 8, letterSpacing: 0.5 }}>SECOND ZOOM (OPTIONAL)</div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>A deeper zoom later in the clip as players gather around the hole. Leave blank to disable.</div>
                        <FormField label="Second Zoom Level">
                            <select style={inputStyle} 
                                value={form.zoom2Level != null ? String(form.zoom2Level) : ''} 
                                onChange={e => set('zoom2Level', e.target.value ? parseFloat(e.target.value) : null)}>
                                <option value="">Disabled</option>
                                <option value="1.5">150%</option>
                                <option value="1.66">166%</option>
                                <option value="2">200%</option>
                            </select>
                        </FormField>
                        {form.zoom2Level && (
                            <FormField label="Second zoom at (% of clip)" hint="Must be after first zoom. e.g. 60">
                                <input style={inputStyle} type="number" min={30} max={90} value={form.zoom2Pct ?? ''}
                                    onChange={e => set('zoom2Pct', parseInt(e.target.value) || null)} />
                            </FormField>
                        )}

                        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 12, marginBottom: 8, letterSpacing: 0.5 }}>END OF CLIP</div>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                            <input type="checkbox" checked={form.zoomResetAtEnd} onChange={e => set('zoomResetAtEnd', e.target.checked)} style={{ marginTop: 2 }} />
                            <div>
                                <div style={{ fontSize: 14 }}>Zoom out to wide shot near end of clip</div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Recommended — creates a clean wide shot as players walk off the green.</div>
                            </div>
                        </label>
                        {form.zoomResetAtEnd && (
                            <FormField label="Start zoom-out (seconds before end)">
                                <input style={inputStyle} type="number" min={5} max={30} value={form.zoomResetSecs}
                                    onChange={e => set('zoomResetSecs', parseInt(e.target.value) || 15)} />
                            </FormField>
                        )}
                    </>
                )}

                {camera.streamType === 4 && (
                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                        <GreenRegionEditor
                            cameraId={camera.id}
                            clubId={clubId}
                            rtmpKey={camera.rtmpKey ?? ''}
                            greenRegionX={form.greenRegionX}
                            greenRegionY={form.greenRegionY}
                            greenRegionW={form.greenRegionW}
                            greenRegionH={form.greenRegionH}
                            onChange={(x, y, w, h) => {
                                set('greenRegionX', x || null);
                                set('greenRegionY', y || null);
                                set('greenRegionW', w || null);
                                set('greenRegionH', h || null);
                            }}
                        />
                    </div>
                )}
            </>
            )}

            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 16, marginBottom: 8, letterSpacing: 0.5 }}>CAMERA ADMIN INFO</div>
            <FormField label="Serial Number"><input style={inputStyle} value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} /></FormField>
            <FormField label="MAC Address"><input style={inputStyle} value={form.macAddress} onChange={e => set('macAddress', e.target.value)} /></FormField>
            <FormField label="LTE Phone Number"><input style={inputStyle} value={form.ltePhoneNumber} onChange={e => set('ltePhoneNumber', e.target.value)} /></FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Reolink Username"><input style={inputStyle} value={form.reolinkUsername} onChange={e => set('reolinkUsername', e.target.value)} autoComplete="off" /></FormField>
                <FormField label="Reolink Password"><input style={inputStyle} type="password" value={form.reolinkPassword} onChange={e => set('reolinkPassword', e.target.value)} autoComplete="off" /></FormField>
            </div>
            <FormField label="Notes"><textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></FormField>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', marginTop: 16, marginBottom: 8, letterSpacing: 0.5 }}>CAMERA OPERATING SCHEDULE</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>When this camera should be capturing. Competitions scheduled outside these hours will trigger an alert. "Open all day" means always available.</div>
            {scheduleLoaded ? <CameraScheduleGrid days={schedule} onChange={setSchedule} /> : <div style={{ fontSize: 13, color: '#999', padding: 10 }}>Loading schedule…</div>}
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
        </>
    );
}

// ── Camera Card ───────────────────────────────────────────

function CameraCard({ camera, clubId, isLive, onUpdated }: { camera: Camera; clubId: string; isLive: boolean; onUpdated: () => void }) {
    const [copied, setCopied] = useState('');
    const [togglingFeatured, setTogglingFeatured] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showAdminInfo, setShowAdminInfo] = useState(false);
    const copy = (text: string, label: string) => { navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 2000); };
    const handleSetFeatured = async () => {
        setTogglingFeatured(true);
        try { await camerasApi.setFeatured(clubId, camera.id, camera.isFeatured ? 'clear-featured' : 'set-featured'); onUpdated(); }
        catch (e: any) { alert(e.message ?? 'Failed to update featured status'); }
        finally { setTogglingFeatured(false); }
    };
    const handleDelete = async () => {
        if (!window.confirm(`Delete camera "${camera.label}"? This will remove it from all competitions and templates.`)) return;
        setDeleting(true);
        try { await camerasApi.delete(clubId, camera.id); onUpdated(); }
        catch (e: any) { alert(e.message ?? 'Failed to delete camera'); }
        finally { setDeleting(false); }
    };
    const isFtpType = camera.streamType === 4;
    const streamTypeLabel = camera.streamType === 1 ? 'Larix' : camera.streamType === 2 ? 'OBS' : camera.streamType === 4 ? 'FTP Clips' : 'Reolink';
    const streamTypeColor = camera.streamType === 1 ? '#818cf8' : camera.streamType === 2 ? '#f97316' : camera.streamType === 4 ? '#0ea5e9' : '#4ade80';
    const connectionFields = isFtpType
        ? [{ label: 'FTP Server', value: 'stream.19thview.com.au', key: 'ftpserver' }, { label: 'FTP Username', value: camera.ftpUsername ?? '', key: 'ftpuser' }, { label: 'FTP Password', value: camera.ftpPassword ?? '', key: 'ftppass' }, { label: 'HLS Playback URL', value: camera.hlsPlaybackUrl, key: 'hls' }]
        : [{ label: 'RTMP URL', value: camera.rtmpUrl, key: 'rtmp' }, { label: 'Stream Key', value: camera.rtmpKey, key: 'key' }, { label: 'HLS Playback URL', value: camera.hlsPlaybackUrl, key: 'hls' }];
    const hasAdminInfo = camera.serialNumber || camera.macAddress || camera.ltePhoneNumber || camera.reolinkUsername || camera.notes;
    return (
        <>
            <div style={styles.cameraCard}>
                <div style={styles.cameraHeader}>
                    <div style={styles.cameraLabel}>
                        {camera.label}
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3, letterSpacing: 0.5, background: `${streamTypeColor}26`, color: streamTypeColor, marginLeft: 8 }}>{streamTypeLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}>Edit</Button>
                        <button style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 4, cursor: togglingFeatured ? 'not-allowed' : 'pointer', letterSpacing: 0.5, border: '1px solid #c9a84c', background: camera.isFeatured ? '#c9a84c' : 'rgba(201,168,76,0.1)', color: camera.isFeatured ? '#000' : '#c9a84c', opacity: togglingFeatured ? 0.5 : 1 }} onClick={handleSetFeatured} disabled={togglingFeatured}>
                            {camera.isFeatured ? '★ Featured' : '☆ Set Featured'}
                        </button>
                        <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? '...' : 'Delete'}</Button>
                        <div style={{ ...styles.liveBadge, background: isFtpType ? (isLive ? '#e8f0ff' : '#f5f5f5') : (isLive ? '#e8f5ee' : '#f5f5f5'), color: isFtpType ? (isLive ? '#3b5bdb' : '#999') : (isLive ? '#1a6b3c' : '#999') }}>
                            {isFtpType ? (isLive ? '● Enabled' : '○ Disabled') : (isLive ? '● Live' : '○ Offline')}
                        </div>
                    </div>
                </div>
                <div style={styles.cameraFields}>
                    {connectionFields.map(f => (
                        <div key={f.key} style={styles.cameraField}>
                            <div style={styles.fieldLabel}>{f.label}</div>
                            <div style={styles.fieldValue}>
                                <code style={styles.code}>{f.value}</code>
                                <button style={styles.copyBtn} onClick={() => copy(f.value, f.key)}>{copied === f.key ? '✓' : 'Copy'}</button>
                            </div>
                        </div>
                    ))}
                </div>
                {hasAdminInfo && (
                    <>
                        <button type="button" style={{ ...styles.advancedToggle, marginTop: 12, paddingLeft: 0 }} onClick={() => setShowAdminInfo(v => !v)}>
                            {showAdminInfo ? '▾' : '▸'} Camera Admin Info
                        </button>
                        {showAdminInfo && (
                            <div style={{ background: '#f0f0f0', borderRadius: 6, padding: 12, marginTop: 8 }}>
                                {camera.serialNumber && <div style={{ fontSize: 13, marginBottom: 6 }}><strong>Serial:</strong> {camera.serialNumber}</div>}
                                {camera.macAddress && <div style={{ fontSize: 13, marginBottom: 6 }}><strong>MAC:</strong> {camera.macAddress}</div>}
                                {camera.ltePhoneNumber && <div style={{ fontSize: 13, marginBottom: 6 }}><strong>LTE:</strong> {camera.ltePhoneNumber}</div>}
                                {camera.reolinkUsername && <div style={{ fontSize: 13, marginBottom: 6 }}><strong>Reolink Login:</strong> {camera.reolinkUsername} / {camera.reolinkPassword ? '••••••••' : '(no password set)'}</div>}
                                {camera.notes && <div style={{ fontSize: 13, marginBottom: 6 }}><strong>Notes:</strong> {camera.notes}</div>}
                            </div>
                        )}
                    </>
                )}
            </div>
            {showEdit && <Modal title="Edit Camera" onClose={() => setShowEdit(false)}><EditCameraForm camera={camera} clubId={clubId} onSaved={() => { setShowEdit(false); onUpdated(); }} onClose={() => setShowEdit(false)} /></Modal>}
        </>
    );
}

// ── Sponsor Row ───────────────────────────────────────────

function SponsorRow({ sponsor, clubId, onUpdated }: { sponsor: Sponsor; clubId: string; onUpdated: () => void }) {
    const [showEdit, setShowEdit] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const handleDelete = async () => {
        if (!window.confirm(`Delete sponsor "${sponsor.name}"?`)) return;
        setDeleting(true);
        try { await sponsorsApi.delete(clubId, sponsor.id); onUpdated(); }
        catch (e: any) { alert(e.message); }
        finally { setDeleting(false); }
    };
    return (
        <>
            <div style={styles.sponsorRow}>
                <div style={styles.sponsorInfo}>
                    {sponsor.logoUrl && <img src={sponsor.logoUrl} alt={sponsor.name} style={styles.sponsorLogo} onError={e => (e.target as HTMLImageElement).style.display = 'none'} />}
                    <div>
                        <div style={styles.sponsorName}>{sponsor.name}</div>
                        <div style={styles.sponsorMeta}>{sponsor.displayFrom && sponsor.displayTo ? `${sponsor.displayFrom} - ${sponsor.displayTo}` : 'Always shown'} · {sponsor.durationSeconds}s</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? '...' : 'Delete'}</Button>
                </div>
            </div>
            {showEdit && <Modal title="Edit Sponsor" onClose={() => setShowEdit(false)}><SponsorForm clubId={clubId} sponsor={sponsor} onSaved={() => { setShowEdit(false); onUpdated(); }} onClose={() => setShowEdit(false)} /></Modal>}
        </>
    );
}

// ── Template Card ─────────────────────────────────────────

function TemplateCard({ template, clubId, cameras, sponsors, isGlobal, onUpdated }: { template: CompetitionTemplate; clubId: string; cameras: Camera[]; sponsors: Sponsor[]; isGlobal: boolean; onUpdated: () => void }) {
    const [showEdit, setShowEdit] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const handleDelete = async () => {
        if (!window.confirm(`Delete template "${template.name}"?`)) return;
        setDeleting(true);
        try { await templatesApi.delete(clubId, template.id); onUpdated(); }
        catch (e: any) { alert(e.message); }
        finally { setDeleting(false); }
    };
    return (
        <>
            <div style={styles.templateCard}>
                <div style={styles.templateHeader}>
                    <div>
                        <div style={styles.templateName}>{template.name}{isGlobal && <span style={styles.globalBadge}>GLOBAL</span>}</div>
                        <div style={styles.templateMeta}>{template.dayOfWeek != null ? DAYS[template.dayOfWeek] : 'Adhoc'} · {FORMATS[template.format]} · {COMP_TYPES[template.type]} · {fmtTime(template.firstTeeTime)} – {fmtTime(template.expectedFinishTime ?? '')}</div>
                        <div style={styles.templateMeta}>Cameras: {template.templateCameras?.map(tc => tc.camera?.label ?? '').join(', ') || 'None'} · Camera delay: {template.cameraStartDelayMinutes}m · Display: {template.leaderboardDisplayMinutes}m after finish</div>
                    </div>
                    {!isGlobal && <div style={{ display: 'flex', gap: 8 }}><Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}>Edit</Button><Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? '...' : 'Delete'}</Button></div>}
                </div>
            </div>
            {showEdit && <Modal title="Edit Template" onClose={() => setShowEdit(false)} width={700}><TemplateForm clubId={clubId} template={template} cameras={cameras} sponsors={sponsors} onSaved={() => { setShowEdit(false); onUpdated(); }} onClose={() => setShowEdit(false)} /></Modal>}
        </>
    );
}

// ── Competition Row ───────────────────────────────────────

function CompetitionRow({ competition, clubId, club, cameras, sponsors, onUpdated }: { competition: Competition; clubId: string; club: Club; cameras: Camera[]; sponsors: Sponsor[]; onUpdated: () => void }) {
    const [showEdit, setShowEdit] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const isToday = new Date(competition.date).toDateString() === new Date().toDateString();
    const isPast = new Date(competition.date) < new Date() && !isToday;
    const handleDelete = async () => {
        if (!window.confirm(`Delete competition "${competition.name}" on ${fmtDate(competition.date)}?`)) return;
        setDeleting(true);
        try { await competitionsApi.delete(clubId, competition.id); onUpdated(); }
        catch (e: any) { alert(e.message); }
        finally { setDeleting(false); }
    };
    return (
        <>
            <div style={{ ...styles.competitionRow, opacity: isPast ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {isToday && <span style={styles.todayBadge}>TODAY</span>}
                    <div>
                        <div style={styles.competitionName}>{competition.name}</div>
                        <div style={styles.competitionMeta}>{fmtDate(competition.date)} · {fmtTime(competition.firstTeeTime)} – {fmtTime(competition.expectedFinishTime ?? '')} · {FORMATS[competition.format]} · {COMP_TYPES[competition.type]}</div>
                        <div style={styles.competitionMeta}>Cameras: {competition.competitionCameras?.map(cc => cameras.find(c => c.id === cc.cameraId)?.label ?? cc.cameraId).join(', ') || 'None assigned'}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? '...' : 'Delete'}</Button>
                </div>
            </div>
            {showEdit && <Modal title="Edit Competition" onClose={() => { setShowEdit(false); onUpdated(); }} width={700}><CompetitionEditForm competition={competition} clubId={clubId} club={club} cameras={cameras} sponsors={sponsors} onSaved={() => { setShowEdit(false); onUpdated(); }} onClose={() => { setShowEdit(false); onUpdated(); }} /></Modal>}
        </>
    );
}

// ── Competition Edit Form ─────────────────────────────────

function CompetitionEditForm({ competition, clubId, club, cameras, sponsors, onSaved, onClose }: {
    competition: Competition; clubId: string; club: Club; cameras: Camera[]; sponsors: Sponsor[]; onSaved: () => void; onClose: () => void;
}) {
    const [form, setForm] = useState({
        name: competition.name, date: competition.date.split('T')[0],
        firstTeeTime: competition.firstTeeTime, lastTeeTime: competition.lastTeeTime,
        expectedFinishTime: competition.expectedFinishTime ?? '',
        cameraStartDelayMinutes: competition.cameraStartDelayMinutes,
        leaderboardDisplayMinutes: competition.leaderboardDisplayMinutes ?? 120,
        format: competition.format, type: competition.type,
        enablePostGameMontage: competition.enablePostGameMontage ?? false,
        cameraIds: competition.competitionCameras?.map(cc => cc.cameraId) ?? [] as string[],
        sponsorIds: competition.competitionSponsors?.map(cs => cs.sponsorId) ?? [] as string[],
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [cameraSponsors, setCameraSponsors] = useState<Record<string, string>>(
        () => Object.fromEntries(
            (competition.competitionCameras ?? [])
                .filter(cc => cc.sponsorId)
                .map(cc => [cc.cameraId, cc.sponsorId!])
        )
    );
    const [savingSponsor, setSavingSponsor] = useState<string | null>(null);

    // Re-sync sponsor dropdowns when parent reloads fresh competition data
    useEffect(() => {
        setCameraSponsors(
            Object.fromEntries(
                (competition.competitionCameras ?? [])
                    .filter(cc => cc.sponsorId)
                    .map(cc => [cc.cameraId, cc.sponsorId!])
            )
        );
    }, [competition]);

    const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
    const toggleCamera = (id: string) => setForm(f => ({ ...f, cameraIds: f.cameraIds.includes(id) ? f.cameraIds.filter(c => c !== id) : [...f.cameraIds, id] }));
    const toggleSponsor = (id: string) => setForm(f => ({ ...f, sponsorIds: f.sponsorIds.includes(id) ? f.sponsorIds.filter(s => s !== id) : [...f.sponsorIds, id] }));

    const handleCameraSponsor = async (cameraId: string, sponsorId: string | null) => {
        setSavingSponsor(cameraId);
        try {
            await competitionsApi.setCameraSponsor(clubId, competition.id, cameraId, sponsorId);
            setCameraSponsors(prev => {
                const next = { ...prev };
                if (sponsorId) next[cameraId] = sponsorId;
                else delete next[cameraId];
                return next;
            });
        } catch (e: any) { alert(e.message ?? 'Failed to save camera sponsor'); }
        finally { setSavingSponsor(null); }
    };

    const handleSubmit = async () => {
        setSaving(true); setError('');
        try { await competitionsApi.update(clubId, competition.id, form); onSaved(); }
        catch (e: any) { setError(e.message ?? 'Failed to save'); }
        finally { setSaving(false); }
    };

    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Competition Name"><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} /></FormField>
                <FormField label="Date"><input style={inputStyle} type="date" value={form.date} onChange={e => set('date', e.target.value)} /></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Format"><select style={inputStyle} value={form.format} onChange={e => set('format', parseInt(e.target.value))}>{FORMATS.map((f, i) => <option key={i} value={i}>{f}</option>)}</select></FormField>
                <FormField label="Type"><select style={inputStyle} value={form.type} onChange={e => set('type', parseInt(e.target.value))}>{COMP_TYPES.map((t, i) => <option key={i} value={i}>{t}</option>)}</select></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <FormField label="First Tee Time"><input style={inputStyle} type="time" value={form.firstTeeTime.substring(0, 5)} onChange={e => set('firstTeeTime', e.target.value + ':00')} /></FormField>
                <FormField label="Last Tee Time"><input style={inputStyle} type="time" value={form.lastTeeTime.substring(0, 5)} onChange={e => set('lastTeeTime', e.target.value + ':00')} /></FormField>
                <FormField label="Expected Finish"><input style={inputStyle} type="time" value={form.expectedFinishTime?.substring(0, 5) ?? ''} onChange={e => set('expectedFinishTime', e.target.value + ':00')} /></FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Camera Start Delay (mins)"><input style={inputStyle} type="number" value={form.cameraStartDelayMinutes} onChange={e => set('cameraStartDelayMinutes', parseInt(e.target.value) || 0)} /></FormField>
                <FormField label="Leaderboard Display (mins)"><input style={inputStyle} type="number" value={form.leaderboardDisplayMinutes} onChange={e => set('leaderboardDisplayMinutes', parseInt(e.target.value) || 120)} /></FormField>
            </div>
            <FormField label="Cameras">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                    {cameras.map(cam => (
                        <div key={cam.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 160 }}>
                                <input type="checkbox" checked={form.cameraIds.includes(cam.id)} onChange={() => toggleCamera(cam.id)} />
                                <span style={{ fontSize: 14 }}>{cam.label}</span>
                            </label>
                            {form.cameraIds.includes(cam.id) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                                    <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Hole Sponsor:</span>
                                    <select
                                        style={{ ...inputStyle, fontSize: 13, padding: '4px 8px', flex: 1, opacity: savingSponsor === cam.id ? 0.5 : 1 }}
                                        value={cameraSponsors[cam.id] ?? ''}
                                        disabled={savingSponsor === cam.id}
                                        onChange={e => handleCameraSponsor(cam.id, e.target.value || null)}
                                    >
                                        <option value="">— No sponsor —</option>
                                        {sponsors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                    {savingSponsor === cam.id && <span style={{ fontSize: 11, color: '#888' }}>Saving…</span>}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </FormField>
            <FormField label="Sponsors">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    {sponsors.map(s => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.sponsorIds.includes(s.id)} onChange={() => toggleSponsor(s.id)} />
                            <span style={{ fontSize: 14 }}>{s.name}</span>
                        </label>
                    ))}
                </div>
            </FormField>
            {club.montageFeatureEnabled && (
                <div style={{ background: '#fff8e1', borderRadius: 8, padding: 14, marginTop: 16, border: '1px solid #f5deb0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.enablePostGameMontage} onChange={e => set('enablePostGameMontage', e.target.checked)} />
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>Enable Post-Game Montage</div>
                            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Archive footage from this competition for the post-game highlights montage. Disable to delete clips automatically after the competition ends.</div>
                        </div>
                    </label>
                </div>
            )}
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
        </>
    );
}

// ── Edit Club Form ────────────────────────────────────────

function EditClubForm({ club, isSuperUser, onSaved, onClose }: { club: Club; isSuperUser: boolean; onSaved: () => void; onClose: () => void }) {
    const [form, setForm] = useState({
        name: club.name, primaryColour: club.primaryColour || '#1a6b3c', timeZone: club.timeZone || 'Australia/Perth',
        isActive: club.isActive, montageFeatureEnabled: club.montageFeatureEnabled ?? false,
        channelDaemonEnabled: club.channelDaemonEnabled ?? false, noticesIntervalMinutes: club.noticesIntervalMinutes ?? 30,
        sponsorAdRatio: club.sponsorAdRatio ?? 3, adMinIntervalMinutes: club.adMinIntervalMinutes ?? 10,
        adFillRatio: club.adFillRatio ?? 0.4, maxAdsPerBreak: club.maxAdsPerBreak ?? 2, platformAdRatio: club.platformAdRatio ?? 4,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingHero, setUploadingHero] = useState(false);
    const [logoUrl, setLogoUrl] = useState(club.logoUrl ?? '');
    const [heroImageUrl, setHeroImageUrl] = useState(club.heroImageUrl ?? '');
    const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Name is required'); return; }
        setSaving(true); setError('');
        try { await clubsApi.update(club.id, form); onSaved(); }
        catch (e: any) { setError(e.message ?? 'Failed to save'); }
        finally { setSaving(false); }
    };
    const handleImageUpload = async (type: 'logo' | 'hero', file: File) => {
        const setUploading = type === 'logo' ? setUploadingLogo : setUploadingHero;
        setUploading(true); setError('');
        try {
            const result = await clubsApi.uploadImage(club.id, type, file);
            if (type === 'logo') setLogoUrl(result.logoUrl ?? result.heroImageUrl ?? '');
            else setHeroImageUrl(result.heroImageUrl ?? result.logoUrl ?? '');
        } catch (e: any) { setError(e.message ?? 'Failed to upload image'); }
        finally { setUploading(false); }
    };
    const timezones = ['Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Brisbane', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Hobart'];
    return (
        <>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <FormField label="Club Name"><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} autoFocus /></FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Club Logo" hint="PNG or JPG, max 10MB">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ ...inputStyle, cursor: 'pointer', background: '#f5f5f5', textAlign: 'center', padding: '8px 12px', fontSize: 13, color: '#555', flexShrink: 0 }}>
                                {uploadingLogo ? 'Uploading…' : logoUrl ? 'Replace Logo' : 'Upload Logo'}
                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingLogo}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload('logo', f); e.target.value = ''; }} />
                            </label>
                            {logoUrl && <span style={{ fontSize: 11, color: '#888', wordBreak: 'break-all' }}>{logoUrl.split('/').pop()}</span>}
                        </div>
                    </div>
                </FormField>
                <FormField label="Hero Image" hint="Shown behind leaderboard, PNG or JPG, max 10MB">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {heroImageUrl && <img src={heroImageUrl} alt="Hero" style={{ maxHeight: 48, maxWidth: 160, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ ...inputStyle, cursor: 'pointer', background: '#f5f5f5', textAlign: 'center', padding: '8px 12px', fontSize: 13, color: '#555', flexShrink: 0 }}>
                                {uploadingHero ? 'Uploading…' : heroImageUrl ? 'Replace Hero' : 'Upload Hero'}
                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingHero}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload('hero', f); e.target.value = ''; }} />
                            </label>
                            {heroImageUrl && <span style={{ fontSize: 11, color: '#888', wordBreak: 'break-all' }}>{heroImageUrl.split('/').pop()}</span>}
                        </div>
                    </div>
                </FormField>
            </div>
            <FormField label="Primary Colour">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={form.primaryColour} onChange={e => set('primaryColour', e.target.value)} style={{ width: 48, height: 40, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                    <input style={{ ...inputStyle, flex: 1 }} value={form.primaryColour} onChange={e => set('primaryColour', e.target.value)} />
                </div>
            </FormField>
            <FormField label="Time Zone">
                <select style={inputStyle} value={form.timeZone} onChange={e => set('timeZone', e.target.value)}>
                    {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
            </FormField>
            <div style={{ background: '#fff8e1', borderRadius: 8, padding: 14, marginTop: 16, border: '1px solid #f5deb0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 10 }}>SUPERUSER FEATURES</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" checked={form.montageFeatureEnabled} onChange={e => set('montageFeatureEnabled', e.target.checked)} />
                    <div><div style={{ fontSize: 14, fontWeight: 600 }}>Post-Game Montage Feature</div><div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Allow this club's competitions to keep recorded footage for post-game highlight montages.</div></div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.channelDaemonEnabled} onChange={e => set('channelDaemonEnabled', e.target.checked)} />
                    <div><div style={{ fontSize: 14, fontWeight: 600 }}>Channel Daemon (Phase 5)</div><div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Use the unified channel architecture (server-side switching, ads, notices). Required for FTP cameras and content insertion.</div></div>
                </label>
            </div>
            {form.channelDaemonEnabled && (
                <>
                    <div style={{ background: '#f0f7f3', borderRadius: 8, padding: 14, marginTop: 12, border: '1px solid #c8e0d2' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 10 }}>NOTICES</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <FormField label="Notices interval (mins)" hint="Min gap between notice plays. Default 30."><input style={inputStyle} type="number" value={form.noticesIntervalMinutes} onChange={e => set('noticesIntervalMinutes', parseInt(e.target.value) || 30)} min={1} /></FormField>
                            <FormField label="Sponsor ad ratio" hint="1 sponsor ad per N break slots. Default 3."><input style={inputStyle} type="number" value={form.sponsorAdRatio} onChange={e => set('sponsorAdRatio', parseInt(e.target.value) || 3)} min={1} /></FormField>
                        </div>
                    </div>
                    {isSuperUser && (
                        <div style={{ background: '#fff8e1', borderRadius: 8, padding: 14, marginTop: 12, border: '1px solid #f5deb0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 10 }}>AD BREAK SETTINGS (SUPERUSER)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <FormField label="Ad min interval (mins)" hint="Rest period between breaks. Default 10."><input style={inputStyle} type="number" value={form.adMinIntervalMinutes} onChange={e => set('adMinIntervalMinutes', parseInt(e.target.value) || 10)} min={1} /></FormField>
                                <FormField label="Ad fill ratio" hint="Fraction of idle slots that trigger a break (0.0–1.0). Default 0.4."><input style={inputStyle} type="number" value={form.adFillRatio} onChange={e => set('adFillRatio', parseFloat(e.target.value) || 0.4)} min={0} max={1} step={0.1} /></FormField>
                                <FormField label="Max ads per break" hint="Maximum ads played per break (1–4). Default 2."><input style={inputStyle} type="number" value={form.maxAdsPerBreak} onChange={e => set('maxAdsPerBreak', parseInt(e.target.value) || 2)} min={1} max={4} /></FormField>
                                <FormField label="Platform ad ratio" hint="1 platform ad per N break slots. Default 4."><input style={inputStyle} type="number" value={form.platformAdRatio} onChange={e => set('platformAdRatio', parseInt(e.target.value) || 4)} min={1} /></FormField>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div style={styles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
        </>
    );
}

// ── Section wrapper ───────────────────────────────────────

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader}><h2 style={styles.sectionTitle}>{title}</h2>{action}</div>
            {children}
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────

export default function ClubDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isSuperUser } = useUserRoles();
    const [club, setClub] = useState<Club | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<Tab>('cameras');
    const [showEditClub, setShowEditClub] = useState(false);
    const [showAddCamera, setShowAddCamera] = useState(false);
    const [showAddSponsor, setShowAddSponsor] = useState(false);
    const [showAddTemplate, setShowAddTemplate] = useState(false);
    const [showApplyTemplate, setShowApplyTemplate] = useState(false);
    const [liveStatus, setLiveStatus] = useState<Record<string, boolean>>({});
    const [templates, setTemplates] = useState<{ globalTemplates: CompetitionTemplate[], clubTemplates: CompetitionTemplate[] }>({ globalTemplates: [], clubTemplates: [] });
    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [compFilter, setCompFilter] = useState<'upcoming' | 'past'>('upcoming');

    const loadClub = useCallback(async () => {
        if (!id) return;
        setLoading(true); setError('');
        try { setClub(await clubsApi.getById(id)); }
        catch (e: any) { setError(e.message ?? 'Failed to load club'); }
        finally { setLoading(false); }
    }, [id]);

    const loadTemplates = useCallback(async () => {
        if (!id) return;
        try { setTemplates(await templatesApi.getForClub(id) as any); }
        catch (e: any) { console.error('Failed to load templates:', e); }
    }, [id]);

    const loadCompetitions = useCallback(async () => {
        if (!id) return;
        try { setCompetitions(await competitionsApi.getForClub(id) as Competition[]); }
        catch (e: any) { console.error('Failed to load competitions:', e); }
    }, [id]);

    useEffect(() => { loadClub(); }, [loadClub]);
    useEffect(() => { if (activeTab === 'templates') loadTemplates(); }, [activeTab, loadTemplates]);
    useEffect(() => { if (activeTab === 'competitions') loadCompetitions(); }, [activeTab, loadCompetitions]);

    useEffect(() => {
        if (!id) return;
        const checkStatus = async () => {
            try {
                const statuses = await clubsApi.getCameraStatus(id);
                const map: Record<string, boolean> = {};
                statuses.forEach((s: any) => { map[s.id] = s.isLive; });
                setLiveStatus(map);
            } catch (err) { console.error('Camera status check failed:', err); }
        };
        checkStatus();
        const interval = setInterval(checkStatus, 15000);
        return () => clearInterval(interval);
    }, [id]);

    const allTemplates = [...templates.globalTemplates, ...templates.clubTemplates];
    const filteredCompetitions = competitions.filter(c => {
        const d = new Date(c.date); const today = new Date(); today.setHours(0, 0, 0, 0);
        return compFilter === 'upcoming' ? d >= today : d < today;
    }).sort((a, b) => compFilter === 'upcoming'
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    if (loading) return <div style={styles.centre}>Loading...</div>;
    if (error) return <div style={styles.errorBanner}>{error}</div>;
    if (!club) return null;

    const displayUrl = `https://display.19thview.com.au/${club.slug}`;

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button style={styles.backBtn} onClick={() => navigate('/admin/clubs')}>Back</button>
                    <div style={{ ...styles.clubDot, background: club.primaryColour || '#1a6b3c' }}>{club.name.charAt(0).toUpperCase()}</div>
                    <div>
                        <h1 style={styles.title}>{club.name}</h1>
                        <span style={{ ...styles.displayLink, cursor: 'pointer' }} onClick={() => window.open(displayUrl, '_blank')}>{displayUrl}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {isSuperUser && <Button variant="secondary" size="sm" onClick={() => setShowEditClub(true)}>Edit Club</Button>}
                    <Button variant={club.isActive ? 'secondary' : 'primary'} size="sm" onClick={async () => { if (club.isActive) await clubsApi.disable(club.id); else await clubsApi.enable(club.id); loadClub(); }}>
                        {club.isActive ? 'Disable Club' : 'Enable Club'}
                    </Button>
                </div>
            </div>

            <TabBar active={activeTab} onChange={setActiveTab} />

            {activeTab === 'cameras' && (
                <>
                    <Section title={`Cameras (${club.cameras?.length ?? 0})`} action={<Button size="sm" onClick={() => setShowAddCamera(true)}>+ Add Camera</Button>}>
                        {(!club.cameras || club.cameras.length === 0) ? <div style={styles.empty}>No cameras configured yet.</div> : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {club.cameras.sort((a, b) => a.sortOrder - b.sortOrder).map(cam => <CameraCard key={cam.id} camera={cam} clubId={club.id} isLive={liveStatus[cam.id] ?? cam.isLive} onUpdated={loadClub} />)}
                            </div>
                        )}
                    </Section>
                    <Section title="Stream Controls">
                        <div style={styles.streamControls}>
                            <div>
                                <div style={styles.fieldLabel}>Manual Override</div>
                                <div style={styles.sponsorMeta}>Enable or disable all cameras immediately.</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button variant="primary" onClick={async () => { await clubsApi.enable(club.id); alert('Streams enabled'); }}>Go Live Now</Button>
                                <Button variant="secondary" onClick={async () => { await clubsApi.disable(club.id); alert('Streams disabled'); }}>End Stream</Button>
                            </div>
                        </div>
                    </Section>
                </>
            )}

            {activeTab === 'sponsors' && (
                <Section title={`Sponsors (${club.sponsors?.length ?? 0})`} action={<Button size="sm" onClick={() => setShowAddSponsor(true)}>+ Add Sponsor</Button>}>
                    {(!club.sponsors || club.sponsors.length === 0) ? <div style={styles.empty}>No sponsors configured yet.</div> : (
                        <div style={styles.sponsorList}>{club.sponsors.sort((a, b) => a.sortOrder - b.sortOrder).map(s => <SponsorRow key={s.id} sponsor={s} clubId={club.id} onUpdated={loadClub} />)}</div>
                    )}
                </Section>
            )}

            {activeTab === 'templates' && (
                <>
                    {templates.globalTemplates.length > 0 && (
                        <Section title="Global Templates">
                            <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Global templates are managed by 19thView and available to all clubs.</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {templates.globalTemplates.map(t => <TemplateCard key={t.id} template={t} clubId={club.id} cameras={club.cameras ?? []} sponsors={club.sponsors ?? []} isGlobal={true} onUpdated={loadTemplates} />)}
                            </div>
                        </Section>
                    )}
                    <Section title="Club Templates" action={<Button size="sm" onClick={() => setShowAddTemplate(true)}>+ New Template</Button>}>
                        {templates.clubTemplates.length === 0 ? <div style={styles.empty}>No club templates yet. Create a template for your regular competitions.</div> : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {templates.clubTemplates.map(t => <TemplateCard key={t.id} template={t} clubId={club.id} cameras={club.cameras ?? []} sponsors={club.sponsors ?? []} isGlobal={false} onUpdated={loadTemplates} />)}
                            </div>
                        )}
                    </Section>
                </>
            )}

            {activeTab === 'competitions' && (
                <Section title="Competitions" action={
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button size="sm" variant="secondary" onClick={() => setShowApplyTemplate(true)} disabled={allTemplates.length === 0}>Apply Template</Button>
                        <Button size="sm" onClick={() => { }}>+ Add Adhoc</Button>
                    </div>
                }>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        {(['upcoming', 'past'] as const).map(f => (
                            <button key={f} style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #ddd', background: compFilter === f ? '#1a6b3c' : '#fff', color: compFilter === f ? '#fff' : '#555', fontSize: 13, cursor: 'pointer', fontWeight: 500 }} onClick={() => setCompFilter(f)}>
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>
                    {filteredCompetitions.length === 0 ? (
                        <div style={styles.empty}>No {compFilter} competitions.{compFilter === 'upcoming' && allTemplates.length > 0 && ' Use "Apply Template" to create competitions from a template.'}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {filteredCompetitions.map(c => <CompetitionRow key={c.id} competition={c} clubId={club.id} club={club} cameras={club.cameras ?? []} sponsors={club.sponsors ?? []} onUpdated={loadCompetitions} />)}
                        </div>
                    )}
                </Section>
            )}

            {activeTab === 'adverts' && <AdvertsTab clubId={club.id} clubSlug={club.slug} isSuperUser={isSuperUser} sponsors={(club.sponsors ?? []).map(s => ({ id: s.id, name: s.name }))} />}

            {showEditClub && <Modal title="Edit Club" onClose={() => setShowEditClub(false)}><EditClubForm club={club} isSuperUser={isSuperUser} onSaved={() => { setShowEditClub(false); loadClub(); }} onClose={() => setShowEditClub(false)} /></Modal>}
            {showAddCamera && <Modal title="Add Camera" onClose={() => setShowAddCamera(false)}><AddCameraForm clubId={club.id} onAdded={() => { setShowAddCamera(false); loadClub(); }} onClose={() => setShowAddCamera(false)} /></Modal>}
            {showAddSponsor && <Modal title="Add Sponsor" onClose={() => setShowAddSponsor(false)}><SponsorForm clubId={club.id} onSaved={() => { setShowAddSponsor(false); loadClub(); }} onClose={() => setShowAddSponsor(false)} /></Modal>}
            {showAddTemplate && <Modal title="New Template" onClose={() => setShowAddTemplate(false)} width={700}><TemplateForm clubId={club.id} cameras={club.cameras ?? []} sponsors={club.sponsors ?? []} onSaved={() => { setShowAddTemplate(false); loadTemplates(); }} onClose={() => setShowAddTemplate(false)} /></Modal>}
            {showApplyTemplate && <Modal title="Apply Template" onClose={() => setShowApplyTemplate(false)}><ApplyTemplateModal clubId={club.id} templates={allTemplates} onApplied={() => { setShowApplyTemplate(false); loadCompetitions(); setActiveTab('competitions'); }} onClose={() => setShowApplyTemplate(false)} /></Modal>}
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    page: { padding: 32, maxWidth: 900, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
    backBtn: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 14, padding: '4px 0' },
    clubDot: { width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 },
    title: { fontSize: 24, fontWeight: 700, margin: 0, marginBottom: 2 },
    displayLink: { fontSize: 13, color: '#1a6b3c', textDecoration: 'none' },
    section: { background: '#fff', borderRadius: 12, border: '1.5px solid #eee', padding: 24, marginBottom: 20 },
    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    sectionTitle: { fontSize: 16, fontWeight: 600, margin: 0 },
    cameraCard: { background: '#f9f9f9', borderRadius: 10, border: '1px solid #eee', padding: 16 },
    cameraHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
    cameraLabel: { fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
    liveBadge: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20 },
    cameraFields: { display: 'flex', flexDirection: 'column', gap: 8 },
    cameraField: {},
    fieldLabel: { fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 },
    fieldValue: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '6px 10px' },
    code: { fontSize: 12, fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' },
    copyBtn: { background: '#f0f0f0', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#555', flexShrink: 0, fontWeight: 600 },
    sponsorList: { display: 'flex', flexDirection: 'column', gap: 1 },
    sponsorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
    sponsorInfo: { display: 'flex', alignItems: 'center', gap: 12 },
    sponsorLogo: { height: 32, maxWidth: 80, objectFit: 'contain', borderRadius: 4 },
    sponsorName: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
    sponsorMeta: { fontSize: 12, color: '#888' },
    templateCard: { background: '#f9f9f9', borderRadius: 10, border: '1px solid #eee', padding: 16 },
    templateHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
    templateName: { fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
    templateMeta: { fontSize: 12, color: '#666', marginBottom: 2 },
    globalBadge: { fontSize: 10, fontWeight: 700, background: '#e8f0ff', color: '#3b5bdb', padding: '2px 8px', borderRadius: 3, letterSpacing: 0.5 },
    competitionRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f0f0f0' },
    competitionName: { fontSize: 14, fontWeight: 600, marginBottom: 3 },
    competitionMeta: { fontSize: 12, color: '#666', marginBottom: 2 },
    todayBadge: { fontSize: 10, fontWeight: 800, background: '#1a6b3c', color: '#fff', padding: '3px 8px', borderRadius: 3, letterSpacing: 0.5, flexShrink: 0 },
    streamControls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
    empty: { color: '#999', fontSize: 14, padding: '20px 0', textAlign: 'center' },
    centre: { padding: 48, textAlign: 'center', color: '#888' },
    errorBanner: { background: '#fff0f0', color: '#c00', border: '1px solid #fcc', borderRadius: 8, padding: '12px 16px', fontSize: 14, marginBottom: 16 },
    formActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 14, paddingBottom: 4, borderTop: '1px solid #eee', marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24 },
    advancedToggle: { background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer', padding: '8px 0', textAlign: 'left', width: '100%' },
};
